import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { generateCompleteCreditPlan } from '../../services/creditPlanService';
import { submitAssessmentForm } from '../../services/assessmentService';
import { trackFeatureUsed, trackCTAClick } from '../../services/analyticsService';
import MarkdownRenderer from './MarkdownRenderer';
import { useRateLimitedFunction } from '../../hooks/useRateLimiter';
import DisclaimerModal from './DisclaimerModal';
import Modal from '../common/Modal';
import './CustomGoalForm.css';

// Word limit for custom goal form text fields to prevent abuse
const ASSESSMENT_WORD_LIMIT = 500;

const CustomGoalForm = ({ onClose, onSubmit, onNavigateToPlan }) => {
  const { currentUser } = useAuth();
  const t = useTranslation();
  const [formData, setFormData] = useState({
    financialGoal: '',
    currentSituation: '',
    timeline: '6-12 months',
    priority: 'medium',
    businessType: '',
    currentCreditScore: '',
    monthlyIncome: '',
    existingDebt: '',
    availableCapital: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [creditPlan, setCreditPlan] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  
  // Rate limit: 3 custom goal submissions per minute to prevent spam
  const { execute: submitWithRateLimit, getRemainingCalls } = useRateLimitedFunction(
    async (data) => await submitAssessmentForm(data),
    3,
    60000
  );

  // Track form open
  useEffect(() => {
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'custom_goal_form',
      action: 'open',
      source: 'modal'
    });
  }, []);

  // Helper function to count words in text
  const countWords = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.financialGoal.trim()) {
      newErrors.financialGoal = t('customGoalForm.goalRequired');
    }
    
    if (!formData.currentSituation.trim()) {
      newErrors.currentSituation = t('customGoalForm.situationRequired');
    }
    
    if (!formData.businessType.trim()) {
      newErrors.businessType = t('customGoalForm.businessTypeRequired');
    }
    
    // Ensure financial goal is not too short
    if (formData.financialGoal.trim().length < 10) {
      newErrors.financialGoal = t('customGoalForm.goalTooShort');
    }
    
    // Ensure current situation is not too short
    if (formData.currentSituation.trim().length < 10) {
      newErrors.currentSituation = t('customGoalForm.situationTooShort');
    }
    
    // Check word limits for textarea fields
    const financialGoalWordCount = countWords(formData.financialGoal);
    if (financialGoalWordCount > ASSESSMENT_WORD_LIMIT) {
      newErrors.financialGoal = `This field exceeds the word limit of ${ASSESSMENT_WORD_LIMIT} words. Please shorten your response.`;
    }
    
    const currentSituationWordCount = countWords(formData.currentSituation);
    if (currentSituationWordCount > ASSESSMENT_WORD_LIMIT) {
      newErrors.currentSituation = `This field exceeds the word limit of ${ASSESSMENT_WORD_LIMIT} words. Please shorten your response.`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Track validation error
      trackFeatureUsed({
        section: 'actionable_plans',
        feature: 'custom_goal_form',
        action: 'validation_error',
        source: 'form'
      });
      return;
    }

    // Check if user is authenticated
    if (!currentUser) {
      setErrors({ submit: 'Please log in to generate a custom credit plan.' });
      return;
    }

    // Check if disclaimer has been accepted
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
      return;
    }

    // Track form submit start
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'custom_goal_form',
      action: 'submit',
      source: 'form',
      additionalParams: {
        timeline: formData.timeline,
        priority: formData.priority,
        business_type: formData.businessType,
        has_credit_score: !!formData.currentCreditScore
      }
    });

    setIsSubmitting(true);
    setGenerationProgress('Generating credit plan...');
    
    try {
      // Create a structured assessment from the free form data
      // Format the data to match what generateCreditPlan expects
      // Generate a consistent goalId that both functions will use
      const goalId = `custom_goal_${Date.now()}`;
      
      const assessmentData = {
        formId: 'custom_goal_form',
        subcategory: 'custom_goal',
        goalId: goalId, // Set goalId at top level for consistency with Firebase functions
        goal: {
          id: goalId, // Set the goalId explicitly to ensure consistency
          goal: formData.financialGoal,
          timeline: formData.timeline
        },
        formData: {
          financial_goal: formData.financialGoal,
          current_situation: formData.currentSituation,
          timeline: formData.timeline,
          priority: formData.priority,
          business_type: formData.businessType,
          current_credit_score: formData.currentCreditScore,
          monthly_income: formData.monthlyIncome,
          existing_debt: formData.existingDebt,
          available_capital: formData.availableCapital,
          // Additional context for AI analysis
          user_context: `Business Type: ${formData.businessType}, Priority: ${formData.priority}, Credit Score: ${formData.currentCreditScore || 'Unknown'}, Monthly Income: ${formData.monthlyIncome || 'Not specified'}, Existing Debt: ${formData.existingDebt || 'Not specified'}, Available Capital: ${formData.availableCapital || 'Not specified'}`
        },
        submittedAt: new Date().toISOString(),
        user_id: currentUser?.uid || 'anonymous',
        form_type: 'custom_goal_form'
      };

      console.log('Submitting custom goal assessment to Firebase:', assessmentData);
      
      // First, submit the assessment for basic analysis (with rate limiting)
      const result = await submitWithRateLimit(assessmentData);
      console.log('AI Analysis received:', result);
      setAiAnalysis(result.data.aiAnalysis);
      
      // Then generate the complete credit plan
      setGenerationProgress(t('assessmentForm.phase1'));
      const planResult = await generateCompleteCreditPlan(assessmentData, result.data.aiAnalysis, setGenerationProgress);
      console.log('Credit plan generated:', planResult);
      
      setCreditPlan(planResult);
      setGenerationProgress(t('assessmentForm.planGenerated'));
      setShowResults(true);
      
      // Track successful plan generation
      trackFeatureUsed({
        section: 'actionable_plans',
        feature: 'custom_goal_form',
        action: 'plan_generated',
        source: 'form',
        additionalParams: {
          timeline: formData.timeline,
          priority: formData.priority,
          plan_steps: planResult?.planStructure ? Object.keys(planResult.planStructure).length - 1 : 0
        }
      });
      
      if (onSubmit) {
        onSubmit({
          ...assessmentData,
          creditPlan: planResult
        });
      }
    } catch (error) {
      console.error('Custom goal assessment submission error:', error);
      
      // Check if it's a rate limit error
      if (error.rateLimited) {
        const seconds = Math.ceil(error.retryAfter / 1000);
        setErrorModal({
          show: true,
          type: 'warning',
          title: t('customGoalForm.rateLimitTitle', 'Rate Limit Exceeded'),
          message: t('customGoalForm.rateLimitExceeded').replace('{seconds}', seconds.toString())
        });
      } else {
        setErrorModal({
          show: true,
          type: 'error',
          title: t('customGoalForm.errorTitle', 'Submission Error'),
          message: t('customGoalForm.errorSubmitting').replace('{error}', error.message)
        });
      }
      
      // Track error
      trackFeatureUsed({
        section: 'actionable_plans',
        feature: 'custom_goal_form',
        action: error.rateLimited ? 'rate_limited' : 'error',
        source: 'form',
        additionalParams: {
          remaining_calls: getRemainingCalls(),
          error: error.message
        }
      });
    } finally {
      setIsSubmitting(false);
      setGenerationProgress('');
    }
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    // Trigger form submission after disclaimer is accepted
    const submitEvent = { preventDefault: () => {} };
    handleSubmit(submitEvent);
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
    // Track disclaimer rejection
    trackFeatureUsed({
      section: 'actionable_plans',
      feature: 'custom_goal_form',
      action: 'disclaimer_rejected',
      source: 'modal'
    }); 
  };

  return (
    <div className="custom-goal-form-overlay">
      <div className="custom-goal-form-modal">
        <div className="custom-goal-form-header">
          <h2>{t('customGoalForm.title')}</h2>
          <button 
            type="button" 
            className="close-button"
            onClick={() => {
              trackFeatureUsed({
                section: 'actionable_plans',
                feature: 'custom_goal_form',
                action: 'close',
                source: 'close_button'
              });
              onClose();
            }}
            disabled={isSubmitting || showResults}
          >
            ×
          </button>
        </div>

        <div className="custom-goal-form-body">
        { (!showResults && !aiAnalysis) && <form onSubmit={handleSubmit} className="custom-goal-form">
          <div className="form-section">
            <h3>{t('customGoalForm.yourFinancialGoal')}</h3>
            <div className="form-group">
              <label htmlFor="financialGoal">
                {t('customGoalForm.describeGoal')}
              </label>
              <div className="form-textarea-wrapper">
                <textarea
                  id="financialGoal"
                  value={formData.financialGoal}
                  onChange={(e) => handleInputChange('financialGoal', e.target.value)}
                  placeholder={t('customGoalForm.goalPlaceholder')}
                  rows={4}
                  disabled={isSubmitting}
                  maxLength={3000}
                />
                <div className={`form-word-count ${countWords(formData.financialGoal) > ASSESSMENT_WORD_LIMIT ? 'form-word-count-error' : countWords(formData.financialGoal) > ASSESSMENT_WORD_LIMIT * 0.9 ? 'form-word-count-warning' : ''}`}>
                  {t('assessmentForm.words').replace('{count}', countWords(formData.financialGoal).toString()).replace('{limit}', ASSESSMENT_WORD_LIMIT.toString())}
                </div>
              </div>
              {errors.financialGoal && (
                <span className="error-message">{errors.financialGoal}</span>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>{t('customGoalForm.currentSituation')}</h3>
            <div className="form-group">
              <label htmlFor="currentSituation">
                {t('customGoalForm.describeSituation')}
              </label>
              <div className="form-textarea-wrapper">
                <textarea
                  id="currentSituation"
                  value={formData.currentSituation}
                  onChange={(e) => handleInputChange('currentSituation', e.target.value)}
                  placeholder={t('customGoalForm.situationPlaceholder')}
                  rows={4}
                  disabled={isSubmitting}
                  maxLength={3000}
                />
                <div className={`form-word-count ${countWords(formData.currentSituation) > ASSESSMENT_WORD_LIMIT ? 'form-word-count-error' : countWords(formData.currentSituation) > ASSESSMENT_WORD_LIMIT * 0.9 ? 'form-word-count-warning' : ''}`}>
                  {t('assessmentForm.words').replace('{count}', countWords(formData.currentSituation).toString()).replace('{limit}', ASSESSMENT_WORD_LIMIT.toString())}
                </div>
              </div>
              {errors.currentSituation && (
                <span className="error-message">{errors.currentSituation}</span>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>{t('customGoalForm.additionalDetails')}</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="businessType">
                  {t('customGoalForm.businessType')}
                </label>
                <input
                  type="text"
                  id="businessType"
                  value={formData.businessType}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  placeholder={t('customGoalForm.businessTypePlaceholder')}
                  disabled={isSubmitting}
                />
                {errors.businessType && (
                  <span className="error-message">{errors.businessType}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="timeline">{t('customGoalForm.timeline')}</label>
                <select
                  id="timeline"
                  value={formData.timeline}
                  onChange={(e) => handleInputChange('timeline', e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="1-3 months">1-3 months</option>
                  <option value="3-6 months">3-6 months</option>
                  <option value="6-12 months">6-12 months</option>
                  <option value="12-24 months">12-24 months</option>
                  <option value="24+ months">24+ months</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="currentCreditScore">
                  {t('customGoalForm.currentCreditScore')}
                </label>
                <input
                  type="text"
                  id="currentCreditScore"
                  value={formData.currentCreditScore}
                  onChange={(e) => handleInputChange('currentCreditScore', e.target.value)}
                  placeholder={t('customGoalForm.creditScorePlaceholder')}
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="priority">{t('customGoalForm.priorityLevel')}</label>
                <select
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="low">{t('customGoalForm.low')}</option>
                  <option value="medium">{t('customGoalForm.medium')}</option>
                  <option value="high">{t('customGoalForm.high')}</option>
                  <option value="urgent">{t('customGoalForm.urgent')}</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="monthlyIncome">
                  {t('customGoalForm.monthlyIncome')}
                </label>
                <input
                  type="text"
                  id="monthlyIncome"
                  value={formData.monthlyIncome}
                  onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
                  placeholder={t('customGoalForm.incomePlaceholder')}
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="existingDebt">
                  {t('customGoalForm.existingDebt')}
                </label>
                <input
                  type="text"
                  id="existingDebt"
                  value={formData.existingDebt}
                  onChange={(e) => handleInputChange('existingDebt', e.target.value)}
                  placeholder={t('customGoalForm.debtPlaceholder')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="availableCapital">
                {t('customGoalForm.availableCapital')}
              </label>
              <input
                type="text"
                id="availableCapital"
                value={formData.availableCapital}
                onChange={(e) => handleInputChange('availableCapital', e.target.value)}
                placeholder={t('customGoalForm.capitalPlaceholder')}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {showPreview && (
            <div className="preview-section">
              <h3>{t('customGoalForm.previewTitle')}</h3>
              <div className="preview-content">
                <div className="preview-item">
                  <strong>{t('customGoalForm.financialGoalLabel')}</strong>
                  <p>{formData.financialGoal || t('customGoalForm.notSpecified')}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.currentSituationLabel')}</strong>
                  <p>{formData.currentSituation || t('customGoalForm.notSpecified')}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.businessTypeLabel')}</strong>
                  <p>{formData.businessType || t('customGoalForm.notSpecified')}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.timelineLabel')}</strong>
                  <p>{formData.timeline}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.priorityLabel')}</strong>
                  <p>{formData.priority}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.creditScoreLabel')}</strong>
                  <p>{formData.currentCreditScore || t('customGoalForm.notSpecified')}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.monthlyIncomeLabel')}</strong>
                  <p>{formData.monthlyIncome || t('customGoalForm.notSpecified')}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.existingDebtLabel')}</strong>
                  <p>{formData.existingDebt || t('customGoalForm.notSpecified')}</p>
                </div>
                <div className="preview-item">
                  <strong>{t('customGoalForm.availableCapitalLabel')}</strong>
                  <p>{formData.availableCapital || t('customGoalForm.notSpecified')}</p>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => {
                trackFeatureUsed({
                  section: 'actionable_plans',
                  feature: 'custom_goal_form',
                  action: 'cancel',
                  source: 'button'
                });
                onClose();
              }}
              className="cancel-button"
              disabled={isSubmitting}
            >
              {t('customGoalForm.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                trackFeatureUsed({
                  section: 'actionable_plans',
                  feature: 'custom_goal_form',
                  action: showPreview ? 'hide_preview' : 'show_preview',
                  source: 'button'
                });
                setShowPreview(!showPreview);
              }}
              className="preview-button"
              disabled={isSubmitting}
            >
              {showPreview ? t('customGoalForm.hidePreview') : t('customGoalForm.showPreview')}
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('customGoalForm.generatingPlan') : t('customGoalForm.generateCustomPlan')}
            </button>
          </div>
        </form> }
        
        {/* Progress Indicator */}
        {isSubmitting && generationProgress && (
          <div className="generation-progress">
            <div className="progress-content">
              <h3>{t('assessmentForm.generatingCreditPlan')}</h3>
              <p>{t('customGoalForm.generatingMessage')}</p>
              <p>{generationProgress}</p>
              <div className="progress-spinner"></div>
            </div>
          </div>
        )}
        
        {/* AI Analysis Results */}
        {showResults && aiAnalysis && (
          <div className="ai-analysis-results">
            <div className="results-header">
              <h3>{t('assessmentForm.aiAnalysisRecommendations')}</h3>
              <button onClick={() => setShowResults(false)} className="close-results">
                ×
              </button>
            </div>
            <div className="analysis-content">
              <div className="analysis-text">
                <MarkdownRenderer 
                  content={aiAnalysis}
                  className="analysis-markdown"
                  showFullText={showFullAnalysis}
                  onToggleText={() => setShowFullAnalysis(!showFullAnalysis)}
                  showToggleButton={true}
                  showMoreText={t('customGoalForm.showMore')}
                  showLessText={t('customGoalForm.showLess')}
                />
              </div>
            </div>
            
            {/* Credit Plan Results */}
            {creditPlan && (
              <div className="credit-plan-results">
                <div className="plan-header">
                  <h3>{t('assessmentForm.personalizedCreditPlan')}</h3>
                  <p>{t('assessmentForm.generatedSteps').replace('{count}', (creditPlan.planStructure ? Object.keys(creditPlan.planStructure).length - 1 : 0).toString())}</p>
                </div>
                <div className="plan-summary">
                  <div className="plan-stats">
                    <div className="stat">
                      <span className="stat-label-form">{t('assessmentForm.goalLabel')}</span>
                      <span className="stat-value">{creditPlan.goal.goal}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label-form">{t('assessmentForm.timelineLabel')}</span>
                      <span className="stat-value">{creditPlan.goal.timeline}</span>
                    </div>
                  </div>
                </div>
                <div className="plan-actions">
                  <button 
                    onClick={() => {
                      // Track navigation to plan
                      trackCTAClick({
                        section: 'actionable_plans',
                        action: 'view_complete_plan',
                        source: 'button',
                        additionalParams: {
                          form_type: 'custom_goal'
                        }
                      });
                      
                      // Navigate to stored assessment history
                      window.location.href = '/history';
                    }} 
                    className="btn-primary"
                  >
                    {t('assessmentForm.viewCompletePlan')}
                  </button>
                </div>
              </div>
            )}
            
          </div>
        )}
        </div>
      </div>
      
      {/* Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleDisclaimerAccept}
        onCancel={handleDisclaimerCancel}
      />
      
      {/* Error Modal */}
      <Modal 
        isOpen={errorModal.show}
        onClose={() => setErrorModal({ show: false, message: '' })}
        type={errorModal.type || 'error'}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

export default CustomGoalForm; 